import { components, paths } from "@api/api";
import useAxios from "@hooks/useFetch";
import { useCallback, useEffect, useMemo } from "react";
import { useCommonDataContext, useGetCategories } from "@core/CommonDataContext";
import {
    HOME_SHOW_QUERY_PARAM,
    HOME_VIEW_AS_QUERY_PARAM,
    HomeShow,
    HomeViewAs,
    mapHomeShowFromApi,
    mapHomeViewAsFromApi
} from "@core/constants";
import { SelectFiltersContextType } from "@vms/SelectFilters/SelectFiltersContext";
import { getUnique } from "@core/utils/array";
import { AffinityAmong, Metric } from "@models/Metric";
import { useGetFromAmongPayload } from "@vms/SelectAmong/useGetAmongPayload";
import { SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import { Sort } from "@models";
import useQueryState from "@hooks/useQueryState";

const SETTINGS_ENDPOINT: keyof paths = '/api/ArtificialIntelligence/GetSettings';
const SETTINGS_AND_ANSWER_ENDPOINT: keyof paths = '/api/ArtificialIntelligence/GetSettingsAndAnswer';
export type AiSearchSettingsResponseData = components['schemas']['FlavorTracker.Entities.DTO.ViewModels.HomePageSettingsAndAnswerViewModel'];
export type AiSearchSettingsAndAnswerRequestData = components["schemas"]["FlavorTracker.Entities.DTO.ArtificialIntelligence.GetAnswerRequest"];
export type AiSearchSettingsAndAnswerResponseData = components['schemas']['FlavorTracker.Entities.DTO.ViewModels.HomePageSettingsAndAnswerViewModel'];
type SettingsModel = components["schemas"]["FlavorTracker.Entities.DTO.ViewModels.HomePageSettingsViewModel"];

export type HomeAiSearchSettings = {
    semanticSearch: string;
    keywordsIds: number[];
    viewAs: HomeViewAs;
    show: HomeShow;
    displayMetric: Metric | null;
    filters: SelectFiltersContextType['appliedFilters'];
    among: SelectAmongData;
    indexTo: SelectAmongData;
    sortByMetric: Metric | null;
    sortByHeatScore: boolean;
    sortByAffinityAmong: AffinityAmong | null;
    sortingDirection: Sort;
    isRequestMtRelated: boolean;
    isRequestCorrelationRelated: boolean;
    isRequestRpRelated: boolean;
}

export type HomeAiSearchResponseModel = {
    answer: string;
    settings: HomeAiSearchSettings | null;
    isAnswerLoaded: boolean;
    isSettingsLoaded: boolean;
    isSettingsFinalized: boolean;
}

function useGetAllSubCategoriesIds() {
    const getCategories = useGetCategories();

    return useCallback(
        (
            categories: SettingsModel['categories'],
            subCategories: SettingsModel['subCategories'],
        ): number[] => {
            const mappedCategories = getCategories(categories ?? []);
            const mappedSubCategoriesIds = mappedCategories
                .flatMap(cat => (cat.subCategories ?? [])
                    .map(subCat => subCat.id ?? 0)
                );

            return  getUnique([
                ...(subCategories ?? []),
                ...mappedSubCategoriesIds,
            ]);
        },
        [getCategories]
    );
}

function useGetAiSearchSettings() {
    const getAllSubCategoriesIds = useGetAllSubCategoriesIds();
    const getFromAmongPayload = useGetFromAmongPayload();

    return useCallback(
        (rawSettings: SettingsModel): HomeAiSearchSettings => {
            const {
                semanticSearch,
                keywords,
                homePageViewType,
                homePageShowType,
                categories,
                subCategories,
                macStages,
                heatScores,
                metric,
                sortingSettings,
                amongModel,
                indexToModel,
            } = rawSettings;

            const viewAs = mapHomeViewAsFromApi(homePageViewType);

            return {
                semanticSearch: semanticSearch ?? '',
                keywordsIds: keywords ?? [],

                viewAs,
                show: mapHomeShowFromApi(homePageShowType),
                displayMetric: metric ?? null,
                among: getFromAmongPayload(amongModel ?? {}, viewAs !== HomeViewAs.People && viewAs !== HomeViewAs.Brands),
                indexTo: getFromAmongPayload(indexToModel ?? {}, viewAs !== HomeViewAs.People && viewAs !== HomeViewAs.Brands),
                filters: {
                    categories: getAllSubCategoriesIds(
                        categories,
                        subCategories
                    ),
                    macStages: macStages ?? [],
                    heatScores: heatScores ?? [],
                },
                sortByHeatScore: sortingSettings?.byHeatScore ?? false,
                sortByMetric: sortingSettings?.metric ?? null,
                sortByAffinityAmong: sortingSettings?.affinityAmong ?? null,
                sortingDirection: sortingSettings?.order === 1 ? Sort.Asc : Sort.Desc,
                isRequestMtRelated: rawSettings.dataRelatedToMenuTrends ?? false,
                isRequestRpRelated: rawSettings.dataRelatedToReportPro ?? false,
                isRequestCorrelationRelated: rawSettings.dataRelatedToCorrelationPage ?? false,
            };
        },
        [getAllSubCategoriesIds, getFromAmongPayload]
    );
}

export function useAiSearchSettingsAndAnswer(query: string): { data: HomeAiSearchResponseModel } {
    const { 1: setViewAs } = useQueryState<HomeViewAs>(HOME_VIEW_AS_QUERY_PARAM, HomeViewAs.Tile);
    const { 1: setShow } = useQueryState<HomeShow>(HOME_SHOW_QUERY_PARAM, HomeShow.Raw);
    const getAiSearchSettings = useGetAiSearchSettings();
    const { keywords } = useCommonDataContext();
    const getAllSubCategoriesIds = useGetAllSubCategoriesIds();

    const settingsResponse = useAxios<AiSearchSettingsResponseData>({
        url: `${SETTINGS_ENDPOINT}?query=${encodeURIComponent(query)}`,
        method: 'get',
        lazy: !query,
    });

    const settingsResponseSettingsRaw = useMemo(
        (): AiSearchSettingsAndAnswerRequestData['settings'] | undefined => {
            return settingsResponse.data?.settings;
        },
        [settingsResponse.data]
    );

    const settingsResponseSettings = useMemo(
        (): AiSearchSettingsAndAnswerRequestData['settings'] | undefined => {
            return settingsResponseSettingsRaw && {
                ...settingsResponseSettingsRaw,
                keywords: (settingsResponseSettingsRaw?.keywords ?? []).filter(i => {
                    const keywordObj = keywords?.[i];
                    const {
                        categories,
                        subCategories,
                        macStages,
                        heatScores,
                    } = settingsResponseSettingsRaw;

                    const settingsSubCategoriesIds = getAllSubCategoriesIds(
                        categories,
                        subCategories,
                    );

                    const keywordSubCategoriesIds = getAllSubCategoriesIds(
                        [],
                        keywordObj?.subCategoriesIds ?? [],
                    );

                    return keywordObj ? (
                        (settingsSubCategoriesIds?.length ? settingsSubCategoriesIds.some(id => keywordSubCategoriesIds.includes(id)) : true) &&
                        (macStages?.length ? macStages.some(stage => keywordObj.macStage === stage) : true) &&
                        (heatScores?.length ? heatScores.some(score => keywordObj.heatScore === score) : true)
                    ) : true;
                }),
            };
        },
        [settingsResponseSettingsRaw, keywords, getAllSubCategoriesIds]
    );

    const settingsAndAnswerPayload = useMemo(
        (): AiSearchSettingsAndAnswerRequestData => ({
            query,
            settings: settingsResponseSettings,
        }),
        [query, settingsResponseSettings]
    );

    const settingsAndAnswerResponse = useAxios<AiSearchSettingsAndAnswerResponseData>({
        url: SETTINGS_AND_ANSWER_ENDPOINT,
        method: 'post',
        payload: settingsAndAnswerPayload,
        lazy: !query || !settingsResponseSettings,
    });

    const answerDataFromSettingsResponse = useMemo(
        (): HomeAiSearchResponseModel['answer'] => {
            return settingsResponse.data?.answer ?? '';
        },
        [settingsResponse.data]
    );

    const settingsDataFromSettingsResponse = useMemo(
        (): HomeAiSearchResponseModel['settings'] => {
            if (!settingsResponseSettings) {
                return null;
            }
            return getAiSearchSettings(settingsResponseSettings);
        },
        [getAiSearchSettings, settingsResponseSettings]
    );

    const answerDataFromSettingsAndAnswerResponse = useMemo(
        (): HomeAiSearchResponseModel['answer'] => {
            return settingsAndAnswerResponse.data?.answer ?? '';
        },
        [settingsAndAnswerResponse.data]
    );

    const settingsDataFromSettingsAndAnswerResponse = useMemo(
        (): HomeAiSearchResponseModel['settings'] => {
            const rawSettings = settingsAndAnswerResponse.data?.settings;
            if (settingsResponseSettings?.isFinalized || !rawSettings) {
                return null;
            }
            return getAiSearchSettings(rawSettings);
        },
        [getAiSearchSettings, settingsAndAnswerResponse.data, settingsResponseSettings]
    );

    const isAnswerLoaded = useMemo(
        (): boolean => {
            let result = false;

            result = result || !!settingsResponse.data?.answer;
            result = result || !!settingsAndAnswerResponse.data?.answer;

            return result;
        },
        [
            settingsResponse.data,
            settingsAndAnswerResponse.data
        ]
    );

    const isSettingsLoaded = useMemo(
        (): boolean => {
            let result = false;

            result = result || isAnswerLoaded;
            result = result || !!settingsDataFromSettingsResponse;
            result = result || !!settingsDataFromSettingsAndAnswerResponse;

            return result;
        },
        [
            isAnswerLoaded,
            settingsDataFromSettingsResponse,
            settingsDataFromSettingsAndAnswerResponse
        ]
    );

    const isSettingsFinalized = useMemo(
        (): boolean => {
            let result = false;

            result = result || isAnswerLoaded;
            result = result || settingsResponseSettings?.isFinalized === true;
            result = result || !!settingsDataFromSettingsAndAnswerResponse;

            return result;
        },
        [
            isAnswerLoaded,
            settingsResponseSettings,
            settingsDataFromSettingsAndAnswerResponse
        ]
    );

    const settings = useMemo(
        () => settingsDataFromSettingsAndAnswerResponse || settingsDataFromSettingsResponse,
        [
            settingsDataFromSettingsResponse,
            settingsDataFromSettingsAndAnswerResponse
        ]
    );

    useEffect(() => {
        if (settings?.viewAs) {
            setViewAs(settings.viewAs);
        }
        if (settings?.show) {
            setShow(settings.show);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    return {
        data: {
            answer: answerDataFromSettingsAndAnswerResponse || answerDataFromSettingsResponse,
            isAnswerLoaded: isAnswerLoaded,
            settings,
            isSettingsLoaded,
            isSettingsFinalized,
        },
    };

}